import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { PagoCombustibleViewModel } from "src/app/_core/models/centros/cebe-contract-list-base.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-pago-combustible-autocomplete",
    templateUrl: "./pago-combustible-autocomplete.component.html",
    styleUrls: ["./pago-combustible-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PagoCombustibleAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class PagoCombustibleAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Pago de Combustible";
	@Input() placeholder =
		"Buscar pago de combustible por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint = "Busque por código o descripción de pago de combustible";

	// Outputs
	@Output() pagoCombustibleSelected =
		new EventEmitter<PagoCombustibleViewModel>();

	// State
	pagosCombustible: PagoCombustibleViewModel[] = [];
	pagoCombustibleId: number | null = null;
	selectedPagoCombustible: PagoCombustibleViewModel | null = null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadPagosCombustible();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.pagoCombustibleId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de pagos de combustible
	 */
	private loadPagosCombustible(): void {
		this.masterService
			.getPagoCombustibleList()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (pagosCombustible) => {
					this.pagosCombustible = pagosCombustible;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar los pagos de combustible",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedPagoCombustible =
			this.pagoCombustibleId != null
				? (this.pagosCombustible.find(
						(p) => p.id === this.pagoCombustibleId,
					) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para un pago de combustible
	 */
	displayPagoCombustible = (
		pagoCombustible: PagoCombustibleViewModel | string,
	): string => {
		if (typeof pagoCombustible === "string") {
			return pagoCombustible;
		}
		return pagoCombustible &&
			pagoCombustible.codigo &&
			pagoCombustible.descripcion
			? `${pagoCombustible.codigo} - ${pagoCombustible.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(pagoCombustible: PagoCombustibleViewModel | null): void {
		this.pagoCombustibleId = pagoCombustible ? pagoCombustible.id : null;
		this.selectedPagoCombustible = pagoCombustible;
		this.onChange(this.pagoCombustibleId);
		this.onTouched();
		if (pagoCombustible) {
			this.pagoCombustibleSelected.emit(pagoCombustible);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
